import {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  RegisterExistingMemberRequest,
  RegisterExistingInstitutionRequest,
  MatchMemberRequest,
  MatchMemberResponse,
  MatchInstitutionRequest,
  MatchInstitutionResponse,
  User,
} from '../types/auth';
import api from '../config/api';

// Backend UserDto has fewer fields than frontend User type
// We'll map the backend response to match frontend expectations
interface BackendUserDto {
  id: string;
  email: string;
  role: string;
  communicationLanguage?: string;
  isActive: boolean;
  emailConfirmed?: boolean;
  profileCompleted?: boolean;
  firstName?: string;
  lastName?: string;
  createdAt: string;
  updatedAt: string;
}

interface BackendLoginResponse {
  user: BackendUserDto;
  token: string;
  refreshToken: string;
}

interface BackendRegisterResponse {
  user?: BackendUserDto | null;
  token?: string | null;
  refreshToken?: string | null;
  requiresEmailConfirmation?: boolean;
  email?: string;
}

interface BackendRegisterCompleteResponse {
  userId: string;
  email: string;
  applicationReferenceNumber?: string;
}

// Helper function to map backend UserDto to frontend User type
// Backend now includes firstName and lastName populated from IndividualMember or Institution
const mapBackendUserToFrontendUser = (backendUser: BackendUserDto): User => {
  return {
    id: backendUser.id,
    email: backendUser.email,
    firstName: backendUser.firstName || '',
    lastName: backendUser.lastName || '',
    role: backendUser.role as any,
    isActive: backendUser.isActive,
    emailConfirmed: backendUser.emailConfirmed,
    profileCompleted: backendUser.profileCompleted,
    createdAt: backendUser.createdAt,
    updatedAt: backendUser.updatedAt,
  };
};

export const authService = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    try {
      const response = await api.post<BackendLoginResponse>('/auth/login', {
        email: credentials.email,
        password: credentials.password,
      });

      const mappedUser = mapBackendUserToFrontendUser(response.data.user);

      return {
        user: mappedUser,
        token: response.data.token,
        refreshToken: response.data.refreshToken,
      };
    } catch (error: any) {
      if (error.response?.status === 401 && error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      if (error.response?.status === 401) {
        throw new Error('Invalid email or password');
      }
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      // Interceptor may have replaced axios error with a plain Error(message); preserve it
      if (error.response === undefined && error instanceof Error && error.message) {
        throw error;
      }
      // No response and no message = real network/connection error
      if (error.response === undefined) {
        throw new Error('NETWORK_ERROR');
      }
      throw new Error(error.message || 'Login failed. Please try again.');
    }
  },

  register: async (userData: RegisterRequest): Promise<RegisterResponse> => {
    try {
      const response = await api.post<BackendRegisterResponse>('/auth/register', {
        email: userData.email,
        password: userData.password,
        role: userData.role,
        communicationLanguage: userData.communicationLanguage,
        registrationType: userData.registrationType,
        existingMemberId: userData.existingMemberId,
        existingInstitutionId: userData.existingInstitutionId,
        firstName: userData.firstName,
        lastName: userData.lastName,
        memberCardNumber: userData.memberCardNumber,
        institutionName: userData.institutionName,
        institutionNameEn: userData.institutionNameEn,
        institutionNameGr: userData.institutionNameGr,
        institutionEmail: userData.institutionEmail,
      });

      if (response.data.requiresEmailConfirmation) {
        return {
          requiresEmailConfirmation: true,
          email: response.data.email ?? userData.email,
        };
      }

      const mappedUser = response.data.user
        ? mapBackendUserToFrontendUser(response.data.user)
        : undefined;
      return {
        user: mappedUser,
        token: response.data.token ?? undefined,
        refreshToken: response.data.refreshToken ?? undefined,
      };
    } catch (error: any) {
      if (error.response?.status === 400) {
        const message = error.response.data?.message || 'Invalid registration data';
        throw new Error(message);
      }
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'Registration failed. Please try again.');
    }
  },

  registerComplete: async (formData: FormData): Promise<{ userId: string; email: string; applicationReferenceNumber?: string }> => {
    try {
      const response = await api.post<BackendRegisterCompleteResponse>(
        '/auth/register-complete',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      return {
        userId: response.data.userId,
        email: response.data.email,
        applicationReferenceNumber: response.data.applicationReferenceNumber,
      };
    } catch (error: any) {
      if (error.response?.status === 400) {
        const message = error.response.data?.message || 'Invalid registration data';
        throw new Error(message);
      }
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'Registration failed. Please try again.');
    }
  },

  registerInstitutionComplete: async (formData: FormData): Promise<{ userId: string; email: string; applicationReferenceNumber?: string }> => {
    try {
      const response = await api.post<BackendRegisterCompleteResponse>(
        '/auth/register-institution-complete',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      return {
        userId: response.data.userId,
        email: response.data.email,
        applicationReferenceNumber: response.data.applicationReferenceNumber,
      };
    } catch (error: any) {
      if (error.response?.status === 400) {
        const message = error.response.data?.message || 'Invalid registration data';
        throw new Error(message);
      }
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'Institution registration failed. Please try again.');
    }
  },

  /** Returns true when the email is not already registered. */
  isEmailAvailable: async (email: string): Promise<boolean> => {
    try {
      const response = await api.get<{ available: boolean }>('/auth/email-available', {
        params: { email: email.trim() },
      });
      return !!response.data?.available;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'Unable to verify email availability.');
    }
  },

  matchMember: async (memberInfo: MatchMemberRequest): Promise<MatchMemberResponse> => {
    try {
      const response = await api.post<MatchMemberResponse>('/auth/match-member', {
        firstName: memberInfo.firstName,
        lastName: memberInfo.lastName,
        memberCardNumber: memberInfo.memberCardNumber,
        email: memberInfo.email,
        dateOfBirth: memberInfo.dateOfBirth,
      });

      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'Member search failed. Please try again.');
    }
  },

  matchInstitution: async (info: MatchInstitutionRequest): Promise<MatchInstitutionResponse> => {
    try {
      const response = await api.post<MatchInstitutionResponse>('/auth/match-institution', {
        institutionName: info.institutionName,
        institutionEmail: info.institutionEmail,
      });

      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'Institution search failed. Please try again.');
    }
  },

  registerExistingMember: async (request: RegisterExistingMemberRequest): Promise<RegisterResponse> => {
    return authService.register(request);
  },

  registerExistingInstitution: async (request: RegisterExistingInstitutionRequest): Promise<RegisterResponse> => {
    return authService.register(request);
  },

  logout: async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } catch (error: any) {
      // Even if logout API call fails, we should still clear local storage
      // This handles cases where token is already invalid or network issues
      console.error('Logout API call failed:', error);
    } finally {
      // Always remove tokens from localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
    }
  },

  getCurrentUser: (() => {
    let inflight: Promise<User | null> | null = null;

    return async (): Promise<User | null> => {
      const token = localStorage.getItem('token');
      if (!token) {
        return null;
      }

      if (inflight) {
        return inflight;
      }

      inflight = (async () => {
        try {
          const response = await api.get<BackendUserDto>('/auth/me');
          return mapBackendUserToFrontendUser(response.data);
        } catch {
          return null;
        } finally {
          inflight = null;
        }
      })();

      return inflight;
    };
  })(),

  confirmEmail: async (userId: string, token: string): Promise<void> => {
    try {
      const response = await api.post('/auth/confirm-email', {
        userId,
        token,
      });

      if (!response.data) {
        throw new Error('Failed to confirm email');
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'Failed to confirm email. Please try again.');
    }
  },

  resendConfirmationEmail: async (email: string): Promise<void> => {
    try {
      const response = await api.post('/auth/resend-confirmation', {
        email,
      });

      if (!response.data) {
        throw new Error('Failed to resend confirmation email');
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'Failed to resend confirmation email. Please try again.');
    }
  },

  forgotPassword: async (email: string): Promise<void> => {
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      if (error.response === undefined) {
        throw new Error('NETWORK_ERROR');
      }
      throw new Error(error.message || 'Failed to request password reset. Please try again.');
    }
  },

  resetPassword: async (payload: {
    userId: string;
    token: string;
    newPassword: string;
    confirmPassword: string;
  }): Promise<void> => {
    try {
      await api.post('/auth/reset-password', {
        userId: payload.userId,
        token: payload.token,
        newPassword: payload.newPassword,
        confirmPassword: payload.confirmPassword,
      });
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      if (error.response === undefined) {
        throw new Error('NETWORK_ERROR');
      }
      throw new Error(error.message || 'Failed to reset password. Please try again.');
    }
  },
};
